/**
 * klubb-order-attrs — puts the club on the order.
 * Traede attributes each B2C sale to a club by the club's Traede customer
 * number (club metaobject field traede_customer_id). The cart lines only
 * carry the slug (_klubb_club), so on the cart page this module writes two
 * cart attributes, klubb_club and traede_customer_id, which Shopify copies
 * to the order as note attributes. The club is taken from the cart lines
 * first (that is what the order will carry), then from the active club.
 * Self-contained module (no relative imports: they bypass ?v= cache-busting).
 * Helpers exported for tests.
 */

export const ATTR_CLUB = 'klubb_club';
export const ATTR_CUSTOMER = 'traede_customer_id';

export function loadClubs(doc) {
  const el = doc.getElementById('klubb-clubs-data');
  if (!el) return [];
  try {
    const list = JSON.parse(el.textContent || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

/** The club the cart belongs to: the first line's _klubb_club, else the active club. */
export function cartClub(cart, clubs, activeClub) {
  const items = (cart && cart.items) || [];
  for (let i = 0; i < items.length; i += 1) {
    const slug = items[i].properties && items[i].properties._klubb_club;
    if (slug) {
      const found = clubs.find((c) => c && c.slug === slug);
      if (found) return found;
    }
  }
  return activeClub || null;
}

export function wantedAttributes(club) {
  const out = {};
  out[ATTR_CLUB] = club && club.slug ? String(club.slug) : '';
  out[ATTR_CUSTOMER] = club && club.traede_customer_id ? String(club.traede_customer_id) : '';
  return out;
}

export function attributesDiffer(current, wanted) {
  const cur = current || {};
  return Object.keys(wanted).some((k) => String(cur[k] == null ? '' : cur[k]) !== wanted[k]);
}

export async function syncOrderAttributes(fetchImpl, doc, activeClub) {
  const res = await fetchImpl('/cart.js', { credentials: 'same-origin' });
  if (!res.ok) return null;
  const cart = await res.json();
  if (!cart.item_count) return null;
  const wanted = wantedAttributes(cartClub(cart, loadClubs(doc), activeClub));
  if (!attributesDiffer(cart.attributes, wanted)) return null;
  const upd = await fetchImpl('/cart/update.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: wanted }),
  });
  return upd.ok ? wanted : null;
}

if (typeof document !== 'undefined' && typeof fetch === 'function') {
  const run = () => {
    const club = (window.klubb && window.klubb.activeClub) || null;
    syncOrderAttributes(fetch.bind(window), document, club).catch(() => {});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  // The club switch dialog re-stamps the lines; pick up the new club too.
  document.addEventListener('klubb:club-changed', run);
  window.addEventListener('pageshow', (e) => { if (e.persisted) run(); });
}
